'use client';

// Shared single-select dropdown for admin-web and bidder-web (consolidated from
// the two per-app copies, BL-596 follow-up / sharing.md § 1).
//
// Built on the WAI-ARIA "select-only combobox" pattern: a `combobox` trigger
// that owns a `listbox` popup, with full keyboard navigation
// (Arrow/Home/End/Enter/Escape/Tab), `aria-activedescendant` highlight
// tracking, outside-click + Escape dismissal, and disabled-option skipping.
//
// The richer admin needs ride the same base as optional props:
//   - `label`            built-in field label
//   - `searchable`       in-dropdown filter input
//   - `size='sm'`        compact trigger/rows
//   - `size='lg'`        44px trigger for touch surfaces
//   - `invalid`          red border + aria-invalid (ADR 016)
//   - option `icon`      leading glyph (e.g. a pulsing "Live" dot)
//   - option `labelNode` rich row markup (styled badge + bold name); `label`
//                        stays the search key + accessible name
//   - option `isSpecial` pinned, accented row (e.g. "Unassigned")
//   - option `isGroupHeader` non-selectable section header
// Bidder passes none of these — it gets the minimal mobile-first dropdown.
//
// `accent` parametrizes the focus-ring / chevron / check color: `secondary`
// (#45d784, admin's theme) is the default so admin's call sites need no new
// prop; bidder passes `accent="primary"` for its teal (#09514d) theme.

import {
    useCallback,
    useContext,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';
import clsx from 'clsx';

import { FieldSizeContext, SHEET_FIELD_BORDER, useFieldLabelTarget } from './FormField';
import { useSelectListbox } from '../hooks/useSelectListbox';
import { computePopoverBox } from '../isomorphic/popover/popover-position';

export interface SelectOption {
    value: string;
    /**
     * Plain-text label — the search key, the accessible name, and the trigger
     * fallback. Always required even when `labelNode` supplies richer markup, so
     * filtering and screen-reader naming stay text-based.
     */
    label: string;
    /**
     * Optional rich rendering for the option row + trigger (e.g. a styled badge
     * plus a bolded name). When present it REPLACES the plain `label` visually;
     * `label` is still used verbatim as the option's accessible name and search
     * key, so keep the two in sync.
     */
    labelNode?: ReactNode;
    /** Disable an individual option — skipped by keyboard nav, not clickable. */
    disabled?: boolean;
    /** Accented row pinned to the top of the list (e.g. "Unassigned"). */
    isSpecial?: boolean;
    /** Non-selectable section header — skipped by keyboard nav. */
    isGroupHeader?: boolean;
    /** Greyed-down row that STAYS selectable (unlike `disabled`) — e.g. an
     *  archived sales rep that may be re-picked where already assigned (BL-880). */
    muted?: boolean;
    /** Optional leading glyph rendered before the label. */
    icon?: ReactNode;
    /**
     * A second line under the label, e.g. why a disabled option can't be
     * picked (BL-2822). Read out as the row's description, never its name.
     */
    description?: string;
}

/** Focus-ring / chevron / check color family. */
export type SelectAccent = 'primary' | 'secondary';

export interface SelectProps {
    /** Currently-selected option value. */
    value: string;
    /** Called with the new value when the user picks an option. */
    onChange: (value: string) => void;
    options: SelectOption[];
    /** Shown on the trigger when no option matches `value`. */
    placeholder?: string;
    /** Classes on the trigger button (sizing, width). */
    className?: string;
    /** Classes on the relative wrapper (positioning the popup). */
    containerClassName?: string;
    /**
     * Popup sizing relative to the trigger.
     * - `'content'` (default): the menu grows to fit its widest option (`w-max`,
     *   min-width = trigger). Matches native-select behavior; a narrow trigger
     *   with long labels widens the menu to show them.
     * - `'trigger'`: the menu is capped at the trigger width, so a long option
     *   truncates instead of overflowing the input's right edge (BUG-957).
     */
    menuWidth?: 'content' | 'trigger';
    /** Built-in field label rendered above the trigger. */
    label?: string;
    /** Render an in-dropdown filter input. */
    searchable?: boolean;
    /** Placeholder for the filter input (when `searchable`). */
    searchPlaceholder?: string;
    /**
     * Trigger height. `default` is 38px, `sm` is the compact trigger that
     * matches a tag badge, and `lg` is 44px — the touch-target minimum, for a
     * control the operator taps with a thumb rather than clicks.
     *
     * `lg` states its height as `min-h-11`, Tailwind's own 2.75rem step and
     * already this repo's admin spelling for a touch target (`Sidebar.tsx`,
     * `MobileNavBar.tsx`, BL-1411). Deliberately NOT `min-h-[44px]`, which is
     * bidder-web's spelling — one more way to say 44 is one more thing to keep
     * in sync.
     *
     * `touch` is the phone-sheet field (BUG-1860): 48px (`min-h-12`) with 16px
     * text, the geometry every control in a full-screen sheet form shares so a
     * dropdown lines up with the text fields beside it. No `sm:` reset — a
     * caller asks for it only while the form is presented as a phone sheet.
     */
    size?: 'default' | 'sm' | 'lg' | 'touch';
    /** Disable the whole control. */
    disabled?: boolean;
    /** Red border + aria-invalid when true (ADR 016). */
    invalid?: boolean;
    /** Focus-ring color family (default `secondary`). */
    accent?: SelectAccent;
    /**
     * Called when the listbox opens (click OR keyboard). Fires exactly once per
     * open action, never on close or re-render. Useful for a caller that wants
     * to react to the dropdown opening — e.g. expanding the row it sits in.
     */
    onOpen?: () => void;
    /** id on the trigger — wires `<label htmlFor>` / FormField. */
    id?: string;
    /**
     * Automation-handle namespace (BL-1874). Yields `<testId>` on the trigger,
     * `<testId>-listbox` on the open popup's list and `<testId>-<option.value>`
     * on each option — keyed on the value, never the label.
     *
     * Every option also carries `data-option-value` (BL-1867) whether or not a
     * namespace is set: it is a key, not a handle (the `data-row-key` /
     * `data-option-id` convention), so `[data-option-value="America/Chicago"]`
     * picks an option even when its value makes an awkward test id.
     *
     * Falls back to `id` when unset (`testId ?? id`, the `NumberInput` row of
     * `.claude/rules/automation-handles.md`); with neither, emits nothing —
     * pages mount several Selects, so a shared default would collide.
     */
    testId?: string;
    /** Accessible name when there's no visible label. */
    'aria-label'?: string;
    /** id of an element describing the control (e.g. inline error). */
    'aria-describedby'?: string;
    /**
     * Receives the trigger button (a `Controller`'s `field.ref`), so a refused
     * submit can focus the dropdown (ADR 016 — Form Validation § 8).
     */
    triggerRef?: (el: HTMLButtonElement | null) => void;
}

/** Color literals per accent — kept as full Tailwind classes so JIT emits them. */
const ACCENT: Record<SelectAccent, { ring: string; open: string; chevron: string; check: string }> = {
    primary: {
        ring: 'focus:ring-[#09514d] focus:border-[#09514d]',
        open: 'border-[#09514d] ring-2 ring-[#09514d] bg-white',
        chevron: 'text-[#09514d]',
        check: 'text-[#09514d]',
    },
    secondary: {
        ring: 'focus:ring-[#45d784] focus:border-[#45d784]',
        open: 'border-[#45d784] ring-2 ring-[#45d784] bg-white',
        chevron: 'text-[#45d784]',
        check: 'text-[#45d784]',
    },
};

// Viewport-collision handling for the portaled popup (BUG-1086). The menu is a
// max-h-60 (240px) scroll list plus a ~52px search field when `searchable`. When
// there isn't room below the trigger, the shared `computePopoverBox` helper
// anchors by the popup's BOTTOM edge so it opens UPWARD instead of hanging off
// the bottom of the screen; we then clamp the list to whatever vertical space
// is available so it always scrolls on screen. The flip math lives once in
// `computePopoverBox` (shared with SearchableSelect.measurePopoverPosition, BUG-988).
const LIST_MAX_HEIGHT = 240; // matches the `max-h-60` list cap
const SEARCH_BOX_HEIGHT = 52; // search field height, added only when `searchable`

/**
 * Viewport-fixed coords for the portaled popup. Anchored by `top` (below the
 * trigger, the default) OR `bottom` (flipped above it when room below is tight).
 * `listMaxHeight` clamps the scroll list to the available space.
 */
type SelectPopupPos = {
    left: number;
    minWidth: number;
    /** Room from `left` to the right safe edge — the menu's ceiling (BUG-1625). */
    maxWidth: number;
    listMaxHeight: number;
} & ({ top: number; bottom?: undefined } | { top?: undefined; bottom: number });

/** A single option (or group-header) row inside the {@link Select} listbox. */
function SelectOptionRow(props: {
    option: SelectOption;
    index: number;
    listboxId: string;
    testId?: string;
    isSelected: boolean;
    isHighlighted: boolean;
    checkClass: string;
    /** The phone-sheet list (BL-2822): 48px rows, 16px text, 20px check. */
    touch: boolean;
    onChoose: (value: string) => void;
    onHover: (index: number) => void;
}) {
    const { option, index, listboxId, testId, isSelected, isHighlighted, checkClass, touch, onChoose, onHover } = props;
    const optionId = `${listboxId}-opt-${index}`;

    if (option.isGroupHeader) {
        return (
            <li
                role="presentation"
                className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 bg-gray-50 border-t border-b border-gray-100 first:border-t-0"
            >
                {option.label}
            </li>
        );
    }

    return (
        <li
            id={optionId}
            data-index={index}
            data-testid={testId ? `${testId}-${option.value}` : undefined}
            data-option-value={option.value}
            role="option"
            aria-selected={isSelected}
            aria-disabled={option.disabled || undefined}
            // When `labelNode` supplies custom markup, pin the accessible name to
            // the plain `label` so screen readers announce it cleanly rather than
            // the concatenated text of the styled spans.
            // A description is read as the row's description (below), so it pins
            // the name to `label` too — otherwise the reason joins the name.
            aria-label={option.labelNode || option.description ? option.label : undefined}
            aria-describedby={option.description ? `${optionId}-description` : undefined}
            onClick={() => !option.disabled && onChoose(option.value)}
            onMouseEnter={() => !option.disabled && onHover(index)}
            className={clsx(
                'flex cursor-pointer items-center justify-between gap-2 px-3 transition-colors',
                touch ? 'min-h-12 py-3 text-base' : 'py-2 text-sm',
                option.disabled && 'cursor-not-allowed text-gray-300',
                option.isSpecial && 'border-b border-gray-100 font-medium',
                !option.disabled && option.isSpecial && isHighlighted && 'bg-purple-50/80',
                !option.disabled && option.isSpecial && (isSelected ? 'bg-purple-50 text-purple-700' : 'text-purple-600 hover:bg-purple-50/50'),
                !option.disabled && !option.isSpecial && isHighlighted && 'bg-gray-100',
                !option.disabled && !option.isSpecial && (isSelected ? 'bg-[#09514d]/5 font-medium text-[#09514d]' : 'text-gray-700 hover:bg-gray-50'),
                // muted overrides the text color family but keeps click/hover behavior.
                !option.disabled && option.muted && 'text-gray-400',
            )}
        >
            {/* `truncate` on a FLEX container does nothing: `text-overflow:
                ellipsis` applies to a block container's inline content, so on a
                flex box only the `overflow: hidden` half lands and a long label
                is chopped mid-word with no "…". It has to sit on a non-flex
                child, and the flex parent needs `min-w-0` to be allowed to
                shrink past its content in the first place. Caught at 390px on a
                real auction name: "DAY 1: SEPTEMBER 23, 2026 - BARRIE ONLINE AU"
                and then nothing. */}
            <span className="flex min-w-0 flex-col">
                <span className="flex min-w-0 items-center gap-2">
                    {option.icon}
                    <span className="truncate">{option.labelNode ?? option.label}</span>
                </span>
                {option.description && (
                    // Off the sheet: admin's 12px help line, as before. On it: 14px,
                    // gray-500 on white 4.83:1 (nothing under 14px on a sheet, ADR 063).
                    <span
                        id={`${optionId}-description`}
                        className={touch ? 'text-sm text-gray-500' : 'ds-help'}
                    >
                        {option.description}
                    </span>
                )}
            </span>
            {isSelected && (
                <Check
                    className={clsx(
                        'flex-shrink-0',
                        touch ? 'h-5 w-5' : 'h-4 w-4',
                        option.isSpecial ? 'text-purple-500' : checkClass,
                    )}
                    aria-hidden="true"
                />
            )}
        </li>
    );
}

/**
 * A custom single-select dropdown: a `combobox` trigger that opens a `listbox`
 * popup. Controlled via `value` / `onChange`. See {@link SelectProps} for the
 * optional admin-side features (label, searchable, size, invalid, special /
 * group-header / icon options).
 *
 * @param props - {@link SelectProps}.
 */
export function Select({
    value,
    onChange,
    options,
    placeholder = 'Select…',
    className,
    containerClassName,
    menuWidth = 'content',
    label,
    searchable = false,
    searchPlaceholder = 'Search...',
    size: sizeProp = 'default',
    disabled = false,
    invalid = false,
    accent = 'secondary',
    onOpen,
    id,
    testId,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    triggerRef,
}: SelectProps) {
    const listboxId = useId();
    const labelId = useId();
    // BL-2108: inside a phone sheet every full-size dropdown is the touch field,
    // so a wrapper that pins `size="default"` still lines up with the sheet's
    // 48px text fields. `sm` is a deliberate compact choice and keeps its size.
    const sheetFieldSize = useContext(FieldSizeContext);
    const size = sheetFieldSize === 'touch' && sizeProp !== 'sm' ? 'touch' : sizeProp;
    // BL-1960: the trigger always carries an id, so the built-in `<label
    // htmlFor>` targets it and clicking "Sales Rep" opens that dropdown. Only a
    // CALLER's id doubles as the automation handle — a generated one would tag
    // every unnamed Select on a page (automation-handles.md).
    const generatedTriggerId = useId();
    const triggerId = id ?? generatedTriggerId;
    const handle = testId ?? id;
    const rootRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    // One trigger, two holders: the popup positions off it, the caller focuses it.
    const setTrigger = useCallback(
        (el: HTMLButtonElement | null) => {
            buttonRef.current = el;
            triggerRef?.(el);
        },
        [triggerRef],
    );
    const listRef = useRef<HTMLUListElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    // Viewport-fixed coords for the portaled popup (null until first measured).
    const [popupPos, setPopupPos] = useState<SelectPopupPos | null>(null);
    const accentClasses = ACCENT[accent];

    const nav = useSelectListbox({
        value,
        options,
        onChange,
        disabled,
        searchable,
        listboxId,
        onOpen,
        rootRef,
        buttonRef,
        listRef,
        popupRef,
    });

    // Anchor the portaled popup under the trigger and keep it there on
    // scroll/resize. The popup renders into <body> (below) so no `overflow`
    // ancestor can clip it; that means we position it ourselves from the
    // trigger's viewport rect instead of relying on `absolute` layout.
    useLayoutEffect(() => {
        if (!nav.isOpen) return;
        const reposition = () => {
            const rect = buttonRef.current?.getBoundingClientRect();
            if (!rect) return;
            // The search field (when present) sits above the scroll list, so it
            // counts toward both the space the menu needs (the flip estimate) and
            // the list's own cap (the clamp below).
            const searchOffset = searchable ? SEARCH_BOX_HEIGHT : 0;
            const box = computePopoverBox({
                triggerRect: rect,
                viewportHeight: window.innerHeight,
                viewportWidth: window.innerWidth,
                estimatedHeight: LIST_MAX_HEIGHT + searchOffset,
                // The menu is at least as wide as its field and grows with its
                // content (`w-max`), so the field's width is what places it; how
                // far it may then grow is `box.maxWidth`, the room to the right
                // safe edge (BUG-1625). Passing a content width is impossible
                // here — the menu has not rendered yet.
                panelWidth: rect.width,
            });
            // Clamp the list to the room actually available (minus the search
            // field) so it always scrolls on screen and never overflows the
            // viewport — even when both directions are tight in a short viewport.
            const listMaxHeight = Math.min(LIST_MAX_HEIGHT, Math.max(0, box.availableHeight - searchOffset));
            // `minWidth` stays the trigger's (unrounded) width so the menu never
            // renders narrower than its field; `box.width` (rounded) is unused here.
            // `computePopoverBox` sets exactly one of top/bottom — branch to keep
            // the discriminated `SelectPopupPos` shape.
            // BUG-1625: `minWidth` is intersected with the room available, not
            // applied flat — CSS resolves `min-width` OVER `max-width`, so a
            // field wider than the safe area (a full-bleed input on a phone)
            // would otherwise force its own menu back off the edge.
            const base = {
                left: box.left,
                minWidth: Math.min(rect.width, box.maxWidth),
                maxWidth: box.maxWidth,
                listMaxHeight,
            };
            setPopupPos(box.bottom !== undefined ? { ...base, bottom: box.bottom } : { ...base, top: box.top! });
        };
        // Coalesce scroll/resize bursts to one reposition per frame so scrolling
        // with the dropdown open doesn't thrash getBoundingClientRect/setState.
        let frame = 0;
        const onScrollResize = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                reposition();
            });
        };
        reposition(); // immediate on open
        // capture=true catches scrolls inside any nested scroll container.
        window.addEventListener('scroll', onScrollResize, true);
        window.addEventListener('resize', onScrollResize);
        return () => {
            if (frame) cancelAnimationFrame(frame);
            window.removeEventListener('scroll', onScrollResize, true);
            window.removeEventListener('resize', onScrollResize);
        };
    }, [nav.isOpen, searchable]);

    // Focus the filter field when the searchable dropdown opens.
    useEffect(() => {
        if (nav.isOpen && searchable) searchInputRef.current?.focus();
    }, [nav.isOpen, searchable]);

    const selectedOption = options.find((opt) => opt.value === value);
    const displayLabel = selectedOption?.label ?? placeholder;

    // The trigger is a `combobox`, which (unlike a `button`) is not named from
    // its content — so an admin Select with neither `label` nor `aria-label`
    // would have no accessible name. Wire the built-in `label` via
    // aria-labelledby, and otherwise fall back to the visible display text so
    // every trigger keeps a name (parity with the old role="button" behavior).
    // BUG-2334: inside a FormField whose `<label for>` points at this trigger, the
    // visible label names it — not the selected value, which `aria-label` would
    // otherwise put ahead of the `for` association.
    const fieldLabel = useFieldLabelTarget();
    const labelledByField = !ariaLabel && !label && fieldLabel?.controlId === triggerId ? fieldLabel.labelId : undefined;
    // BUG-2335: any other `<label for>` aimed at the trigger names it too, so the
    // value fallback steps aside once such a label is in the document. A Select
    // nothing labels keeps the fallback so it never has an empty name.
    const [hasForLabel, setHasForLabel] = useState(false);
    // Scans once per trigger id, and only when nothing else already names it. A label
    // added later in a separate commit than the Select is not seen until its id changes.
    const needsLabelScan = !ariaLabel && !label && !labelledByField;
    useLayoutEffect(() => {
        if (!needsLabelScan) {
            setHasForLabel(false);
            return;
        }
        const root = rootRef.current?.getRootNode() as Document | ShadowRoot | undefined;
        // Compare htmlFor directly: ids from useId contain colons, so no selector escaping is needed.
        const labels = root?.querySelectorAll('label[for]') ?? [];
        setHasForLabel(Array.from(labels).some((l) => (l as HTMLLabelElement).htmlFor === triggerId));
    }, [needsLabelScan, triggerId]);
    const computedAriaLabel = ariaLabel ?? (label || labelledByField || hasForLabel ? undefined : displayLabel);

    return (
        <div className={clsx('relative', containerClassName)} ref={rootRef}>
            {label && (
                <label id={labelId} htmlFor={triggerId} className="mb-1 block text-sm font-medium text-gray-700">
                    {label}
                </label>
            )}
            <button
                ref={setTrigger}
                id={triggerId}
                data-testid={handle}
                type="button"
                role="combobox"
                disabled={disabled}
                onClick={() => (nav.isOpen ? nav.close() : nav.open())}
                onKeyDown={(e) => nav.handleKeyDown(e)}
                aria-haspopup="listbox"
                aria-expanded={nav.isOpen}
                aria-controls={nav.isOpen ? listboxId : undefined}
                aria-activedescendant={nav.activeDescendant}
                aria-invalid={invalid || undefined}
                aria-label={computedAriaLabel}
                aria-labelledby={label ? labelId : labelledByField}
                aria-describedby={ariaDescribedBy}
                className={clsx(
                    'w-full border text-left transition-all focus:outline-none focus:ring-2',
                    accentClasses.ring,
                    size === 'sm' && 'rounded-md px-2.5 py-1',
                    // The 44px thumb floor below `sm`, matching `TextInput` and
                    // `NumberInput`, so a form on a phone lines up instead of
                    // mixing 44px fields with 38px dropdowns. `lg` already
                    // carries `min-h-11` unconditionally (that one is a
                    // container-width decision, not a viewport one); `sm` is the
                    // deliberately compact variant and keeps its own height.
                    // The reset names BOTH apps' first tier (BL-1743 RAC 1):
                    // admin keeps Tailwind's `sm` (640px); bidder-web replaces
                    // its screens with `tablet` (768px) and has no `sm`, so
                    // `sm:min-h-0` alone emitted nothing there and the floor
                    // leaked onto desktop. Each app ignores the other's prefix.
                    size === 'default' && 'rounded-lg px-3 py-2 min-h-11 sm:min-h-0 tablet:min-h-0',
                    // `min-h-11` is a floor, not a height: the padding still sets
                    // the natural 38px and the floor lifts it to 44. `flex`
                    // re-centres the row once the floor is what decides the
                    // height — without it the extra 6px all lands at the bottom.
                    size === 'lg' && 'flex items-center rounded-lg px-3 py-2 min-h-11',
                    size === 'touch' && 'flex items-center rounded-lg px-3 py-2 min-h-12',
                    disabled
                        ? 'cursor-not-allowed border-gray-300 bg-gray-50 opacity-60'
                        : nav.isOpen
                            ? accentClasses.open
                            : invalid
                                ? 'border-red-600 bg-white hover:border-red-700'
                                // A phone keeps :hover after a tap, so the sheet's light border takes no hover darkening.
                                : clsx('bg-white', size === 'touch' ? SHEET_FIELD_BORDER : 'border-gray-300 hover:border-gray-400'),
                    className,
                )}
            >
                <div className="flex w-full items-center justify-between">
                    <span
                        className={clsx(
                            // Same flex/ellipsis trap as the option row above —
                            // the truncation lives on the inner span.
                            'flex min-w-0 items-center gap-2',
                            size === 'sm' ? 'text-xs' : size === 'touch' ? 'text-base' : 'text-sm',
                            selectedOption?.isSpecial
                                ? 'font-medium text-purple-600'
                                : selectedOption
                                    ? 'text-gray-900'
                                    : 'text-gray-400',
                        )}
                    >
                        {selectedOption?.icon}
                        <span className="truncate">{selectedOption?.labelNode ?? displayLabel}</span>
                    </span>
                    <ChevronDown
                        className={clsx(
                            'ml-1 flex-shrink-0 text-gray-400 transition-transform',
                            size === 'sm' ? 'h-3 w-3' : 'h-4 w-4',
                            nav.isOpen && clsx('rotate-180', accentClasses.chevron),
                        )}
                        aria-hidden="true"
                    />
                </div>
            </button>

            {nav.isOpen && typeof document !== 'undefined' && createPortal(
                <div
                    ref={popupRef}
                    // Dynamic viewport coords (getBoundingClientRect) — the
                    // sanctioned inline-style exception per ui-standards.md.
                    style={{
                        position: 'fixed',
                        // Exactly one of top/bottom is set: `top` anchors below the
                        // trigger (default), `bottom` flips the menu above it when
                        // room below is tight (BUG-1086).
                        top: popupPos?.top,
                        bottom: popupPos?.bottom,
                        left: popupPos?.left ?? 0,
                        minWidth: popupPos?.minWidth,
                        // menuWidth='trigger' caps the menu at the trigger width so a
                        // long option truncates instead of overflowing the input's right
                        // edge (BUG-957). `w-max` + min-width + this max-width all resolve
                        // to exactly the trigger width.
                        //
                        // BUG-1625: 'auto' is no longer UNCAPPED — it is capped at the
                        // room to the right safe edge. That is what stops a long option
                        // label carrying the menu off the screen, and it does not shrink
                        // the mode on a desktop, where the room is far wider than any
                        // menu. Both branches intersect with the same ceiling, so neither
                        // can paint past it.
                        maxWidth:
                            menuWidth === 'trigger'
                                ? Math.min(popupPos?.minWidth ?? 0, popupPos?.maxWidth ?? 0)
                                : popupPos?.maxWidth,
                        // Float above modals/panels — both this popup and a
                        // SlideOutPanel/modal portal to <body>, so the higher
                        // z-index wins. 50 sat BELOW the lot panel's z-51 and the
                        // menu rendered behind it (BUG-979). Matches the sibling
                        // portaled popups (DateTimePicker, StatusBadgeSelect = 9999).
                        zIndex: 9999,
                        // Hide the first pre-measure frame so it never flashes at (0,0).
                        visibility: popupPos ? 'visible' : 'hidden',
                    }}
                    className="w-max overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                >
                    {searchable && (
                        <div className="border-b border-gray-100 p-2">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={nav.searchQuery}
                                    onChange={(e) => nav.setSearchQuery(e.target.value)}
                                    onKeyDown={(e) => nav.handleKeyDown(e, true)}
                                    placeholder={searchPlaceholder}
                                    aria-label={searchPlaceholder}
                                    aria-controls={listboxId}
                                    aria-activedescendant={nav.activeDescendant}
                                    className={clsx(
                                        'w-full rounded border border-gray-200 py-1.5 pl-8 pr-3 text-sm focus:outline-none focus:ring-1',
                                        accentClasses.ring,
                                    )}
                                />
                            </div>
                        </div>
                    )}

                    <ul
                        ref={listRef}
                        id={listboxId}
                        data-testid={handle ? `${handle}-listbox` : undefined}
                        role="listbox"
                        aria-label={ariaLabel}
                        // Clamp to the available viewport space so the list always
                        // scrolls on screen; falls back to the max-h-60 cap pre-measure.
                        style={{ maxHeight: popupPos?.listMaxHeight }}
                        className="max-h-60 overflow-auto py-1"
                    >
                        {nav.renderOrder.map((option, index) => (
                            <SelectOptionRow
                                key={option.isGroupHeader ? `header-${option.value}` : option.value}
                                option={option}
                                index={index}
                                listboxId={listboxId}
                                testId={handle}
                                isSelected={!option.isGroupHeader && option.value === value}
                                isHighlighted={index === nav.highlightedIndex}
                                checkClass={accentClasses.check}
                                touch={size === 'touch'}
                                onChoose={nav.choose}
                                onHover={nav.setHighlightedIndex}
                            />
                        ))}
                        {searchable && nav.renderOrder.length === 0 && (
                            <li role="presentation" className="px-3 py-4 text-center text-sm text-gray-500">
                                No results found
                            </li>
                        )}
                    </ul>
                </div>,
                document.body,
            )}
        </div>
    );
}
